import React from 'react';
import * as yup from 'yup';
import { useSparkBuilderStore } from 'stores';
import { Avatar, Box, CircularProgress } from '@mui/material';
import { SuccessStatusIcon, WarningStatusIcon } from 'components/SVG';
import { InfoCard, SparkAccordion } from 'sharedComponents/UI';
import { SparkForm, SparkFormButton } from 'sharedComponents/SparkForm';
import PromptFunctionFormContent from './PromptFunctionFormContent';
import { useUiComponents } from 'hooks/SparkFormBuilder';
import { useBoardNodeTypes } from 'hooks/EntityTypes/useBoardNodeTypes';
import type { DialogConfig } from 'hooks/common/useDialog';
import {
  useAIModelByID,
  useAIModelExecutionParameters,
  useAIProviders,
} from 'hooks/PromptFunction';
import {
  useGetPromptFunctionsByID,
  useUpdatePromptFunctions,
} from 'hooks/SparkBuilder/promptFunctions';
import { evaluateInitialValue } from 'utilities/form';
import { parseJSONCleaned } from 'utilities/index';
import { isEmptyObject } from 'utilities/object';
import type {
  PromptFunctions,
  PromptFunctionsFormValueType,
} from 'types/PromptFunctions';
import { useSparkOutputReferences } from 'hooks/SparkBuilder/sparkStepReferences';
import { STEP_TYPE_NAMES } from 'constants/SparkSteps';

export type AttachmentsType = {
  id: string;
  type: string;
  title: string;
  fileTypeTag: string;
  thumbnail?: string;
  details?: string;
};

type PromptAccordionProps = {
  promptFunctionID: PromptFunctions['id'];
  dialogConfig: DialogConfig;
  onShowStepChange?: (showStep: boolean) => void;
  onAfterSubmit?: () => void;
  memoryAttachments?: AttachmentsType[];
  hasIssues?: boolean;
};

const getIsMultiSelectField = (enumValue: string | undefined | null) => {
  if (!enumValue) return false;
  const parsedEnumValues = parseJSONCleaned<any>(enumValue);
  return parsedEnumValues?.selectionType === 'multi';
};

function PromptAccordion({
  promptFunctionID,
  dialogConfig,
  onShowStepChange,
  onAfterSubmit,
  memoryAttachments = [],
  hasIssues = false,
}: PromptAccordionProps) {
  const [selectedAiModelID, setSelectedAiModelID] = React.useState<
    string | null
  >(null);
  const [selectedProviderId, setSelectedProviderId] = React.useState('');
  const { isLoading: isUiComponentLoading } = useUiComponents();

  const selectedStep = useSparkBuilderStore(
    (store) => store.builderSideBar.selectedStep
  );

  const sparkID = useSparkBuilderStore((store) => store.sparkID);
  const sparkStep = useSparkBuilderStore((store) =>
    store.sparkStepsActions.getSparkStep(selectedStep?.id || '')
  );

  const { allReferencesIdMap } = useSparkOutputReferences(sparkID);

  const { data: promptFunctions, isLoading: isPromptFunctionsLoading } =
    useGetPromptFunctionsByID(promptFunctionID);

  const {
    dropdownOptions: boardNodeTypesOptions,
    isLoading: isLoadingBoardNodes,
  } = useBoardNodeTypes();

  const {
    mutateAsync: updatePromptFunction,
    isLoading: isPromptFunctionLoading,
  } = useUpdatePromptFunctions(promptFunctionID);

  const {
    prompt,
    instructions,
    aiModelID,
    executionSettings,
    outputBoardNodeTypeID,
  } = promptFunctions?.data?.attributes || {};

  const { aiModel, isLoading: isAiModelLoading } = useAIModelByID(aiModelID);
  const { isLoading: isAiProviderOptionsLoading } = useAIProviders();
  const { joinedAiModelParameter } =
    useAIModelExecutionParameters(selectedAiModelID);

  const hasPromptIssues =
    sparkStep?.meta.diagnostics.hasPromptIssues ||
    sparkStep?.meta.diagnostics.hasMemoryQueryIssues ||
    false;

  const sparkStepTypeIdMap = useSparkBuilderStore(
    (store) => store.stepTypeIdMap
  );
  const functionType =
    sparkStep && sparkStepTypeIdMap?.[sparkStep.attributes.sparkStepTypeID];
  const functionName = functionType?.attributes.key || '';

  const handleAiModelChange = React.useCallback(
    (aiModelId: string | number) => {
      setSelectedAiModelID(`${aiModelId}`);
    },
    []
  );

  const handleProviderChange = React.useCallback(
    (providerId: string | number) => {
      setSelectedProviderId(`${providerId}`);
      handleAiModelChange('');
    },
    [handleAiModelChange]
  );

  const handleFormSubmit = async (formValues: PromptFunctionsFormValueType) => {
    if (!selectedStep?.id) return;

    const {
      stepTitle,
      instructions,
      aiModel,
      prompt,
      outputBoardNodeTypeID,
      attachments,
      ...restFormValues
    } = formValues;

    const items = attachments.map((item) => {
      if (item.type === 'memory') {
        return {
          type: 'memory',
          id: item.id,
        };
      }
      return item.id;
    });

    await updatePromptFunction({
      instructions,
      name: stepTitle,
      prompt,
      outputBoardNodeTypeID,
      items: JSON.stringify(items),
      executionSettings:
        outputBoardNodeTypeID === 'custom'
          ? JSON.stringify({
              ExtensionData: restFormValues,
            })
          : '',
      ...(aiModel && { aiModelID: aiModel }),
    });

    if (onAfterSubmit) {
      onAfterSubmit();
    }
  };

  React.useEffect(() => {
    if (selectedProviderId || !aiModelID) {
      return;
    }

    if (!selectedAiModelID) {
      handleAiModelChange(aiModelID);
    }
    if (aiModel?.attributes.aiProviderID) {
      setSelectedProviderId(aiModel?.attributes.aiProviderID);
    }
  }, [
    aiModel?.attributes.aiProviderID,
    aiModelID,
    handleAiModelChange,
    selectedAiModelID,
    selectedProviderId,
  ]);

  React.useEffect(() => {
    if (onShowStepChange) {
      onShowStepChange(!!prompt);
    }
  }, [prompt, onShowStepChange]);
  const executionSettingsValues = parseJSONCleaned<any>(
    executionSettings || ''
  );
  const extensionData = executionSettingsValues?.ExtensionData;
  console.log('extensionData,', extensionData, selectedAiModelID === aiModelID);

  const initialConfigFormValue: any = React.useMemo(() => {
    if (!isEmptyObject(extensionData) && selectedAiModelID === aiModelID)
      return (
        joinedAiModelParameter?.reduce((acc, curr) => {
          return {
            ...acc,
            [curr?.parameterAttributes?.key]: evaluateInitialValue(
              extensionData?.[curr?.parameterAttributes?.key + '']
            ),
          };
        }, {}) || {}
      );
    else {
      return (
        joinedAiModelParameter?.reduce((acc, curr) => {
          return {
            ...acc,
            [curr?.parameterAttributes?.key]: evaluateInitialValue(
              curr.parameterAttributes.defaultValue
            ),
          };
        }, {}) || {}
      );
    }
  }, [aiModelID, extensionData, joinedAiModelParameter, selectedAiModelID]);

  const configValidationFieldsSchema = React.useMemo(() => {
    if (!joinedAiModelParameter) {
      return {};
    }

    return (
      joinedAiModelParameter.reduce((acc, curr) => {
        const { parameterAttributes } = curr;
        const isMultiSelectField = getIsMultiSelectField(
          parameterAttributes.enumValues
        );

        if (isMultiSelectField) {
          return {
            ...acc,
            [parameterAttributes?.key]: parameterAttributes?.required
              ? yup
                  .array(yup.string())
                  .min(1)
                  .required(`${parameterAttributes?.headerText} is required`)
              : yup
                  .array(yup.string())
                  .typeError(
                    `${parameterAttributes?.headerText} should be string`
                  ),
          };
        }
        return {
          ...acc,
          [parameterAttributes?.key]: parameterAttributes?.required
            ? yup
                .string()
                .required(`${parameterAttributes?.headerText} is required`)
            : yup
                .string()
                .typeError(
                  `${parameterAttributes?.headerText} should be string`
                ),
        };
      }, {}) || {}
    );
  }, [joinedAiModelParameter]);

  const items = React.useMemo(() => {
    const itemsMap = parseJSONCleaned<any[]>(
      promptFunctions?.data.attributes.items || ''
    );

    if (!itemsMap || !Array.isArray(itemsMap)) return;

    const isObjectArray = typeof itemsMap[0] === 'object' && itemsMap[0]?.type;

    if (isObjectArray) {
      const hasMemoryType = itemsMap.some((item) => item?.type === 'memory');
      if (hasMemoryType) return;
    }

    return itemsMap.map((item) => allReferencesIdMap?.[item]);
  }, [promptFunctions?.data.attributes.items, allReferencesIdMap]);

  const promptAttachements = React.useMemo(() => {
    if (items && items?.length > 0) {
      return items?.reduce((acc, item) => {
        const alreadyExists = acc.some(
          (attachment) => attachment.id === item.id
        );
        if (alreadyExists) return acc;

        const fileData = item?.value && JSON.parse(item?.value);
        if (fileData && fileData.length > 0 && fileData[0].name) {
          const fileName = fileData[0].name;
          const fileType = fileData[0].type;
          const lastDotIndex = fileName.lastIndexOf('.');
          const fileTypeTag = fileName
            .substring(lastDotIndex + 1)
            .toLowerCase();

          acc.push({
            id: item.reference,
            title: fileName,
            type: 'file',
            details: fileType.startsWith('image/') ? 'Image' : 'Document',
            fileTypeTag,
            thumbnail: item.iconURL,
          });
        }

        return acc;
      }, [] as AttachmentsType[]);
    }
  }, [items]);

  const validationSchema = yup.object().shape({
    prompt: yup.string().required('Prompt field is Required'),
    instructions: yup.string().notRequired(),
    provider: yup.string().required('Provider field is Required'),
    aiModel: yup.string().required('AI Model field is Required'),
    outputBoardNodeTypeID: yup.string().required('Output field is Required'),
    attachments: yup.array().optional(),
    ...configValidationFieldsSchema,
  });

  const initialValues = {
    prompt: prompt || '',
    instructions: instructions || '',
    stepTitle: sparkStep?.attributes.name || '',
    aiModel: selectedAiModelID || aiModelID || '',
    provider: selectedProviderId || '',
    outputBoardNodeTypeID: outputBoardNodeTypeID
      ? outputBoardNodeTypeID + ''
      : 'custom',
    attachments:
      promptAttachements && promptAttachements.length > 0
        ? promptAttachements
        : memoryAttachments,
    ...initialConfigFormValue,
  };
  const isLoading =
    isPromptFunctionsLoading ||
    isUiComponentLoading ||
    isLoadingBoardNodes ||
    isAiModelLoading ||
    isAiProviderOptionsLoading;

  const disableSubmitButton = isPromptFunctionLoading || hasIssues;

  if (isLoading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' p={2}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  console.log('initialConfigFormValue,', initialConfigFormValue);
  return (
    <SparkAccordion
      title={
        functionName === STEP_TYPE_NAMES.MEMORY ? 'Knowledge Prompt' : 'Prompt'
      }
      sx={{ border: 'none' }}
      dialogConfig={dialogConfig}
      accordionTitleProps={{ variant: 'h5' }}
      summaryStyles={{ paddingX: 2, paddingY: 1, cursor: 'pointer' }}
      detailsStyle={{ paddingBottom: 2 }}
      icon={
        <Avatar
          sx={{
            width: '30px',
            height: '30px',
          }}
        >
          {hasPromptIssues ? (
            <WarningStatusIcon width='100%' height='100%' />
          ) : (
            <SuccessStatusIcon width='100%' height='100%' />
          )}
        </Avatar>
      }
    >
      {hasIssues && (
        <Box p={2}>
          <InfoCard
            type='warning'
            info='The source is not active'
            cardStyles={{ width: '100%' }}
          />
        </Box>
      )}
      <SparkForm<PromptFunctionsFormValueType>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSave={handleFormSubmit}
        sx={{ paddingX: 2, paddingY: 1.5 }}
        enableReinitialize={true}
      >
        <PromptFunctionFormContent
          onProviderChange={handleProviderChange}
          onChangeAiModel={handleAiModelChange}
          outputDropdownOptions={boardNodeTypesOptions}
        />
        <SparkFormButton
          sx={{
            alignSelf: 'flex-end',
            paddingY: 1.5,
            paddingX: 3,
          }}
          disabled={disableSubmitButton}
        >
          {isPromptFunctionLoading ? 'Submitting...' : 'Continue'}
        </SparkFormButton>
      </SparkForm>
    </SparkAccordion>
  );
}

export default PromptAccordion;
